import { Subject } from 'rxjs';
import { startWith, filter, takeUntil } from 'rxjs/operators';

import { coerceBooleanProperty } from '@angular/cdk/coercion';
import {
  OnInit,
  Component,
  Attribute,
  OnDestroy,
  ViewChild,
  TemplateRef,
  ChangeDetectorRef,
  ChangeDetectionStrategy,
  Output,
  EventEmitter,
  Input,
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';

import { ISnComponentOrTemplateConfig } from '@lib/common/interfaces';
import { SnScreenService, SnViewport } from '@lib/common/services';
import { SnComponentOrTemplate } from '@lib/common/types';
import { SnRouterHelperService, mapToRouteDataItem } from '@lib/state/router';
import { SnDrawerService, SnDrawerRef } from '@lib/ui/drawer';

import { ISnLayoutNavbarConfig } from '../../interfaces';
import { SnLayoutNavbarConfig } from '../../models';
import { SnLayoutStorageService } from '../../services';
import { SnLayoutNavbarMode } from '../../types';

@Component({
  selector: 'sn-layout-navbar',
  templateUrl: './layout-navbar.container.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SnLayoutNavbarContainer implements OnInit, OnDestroy {
  @Input()
  public version: string;

  @Output() public readonly navbarCollapsed = new EventEmitter<boolean>(false);
  @Output() public readonly navbarOpened = new EventEmitter<boolean>(false);

  public readonly isDark: boolean;

  @ViewChild('drawerTmpl', { static: true })
  private readonly _drawerTmpl: TemplateRef<any>;

  private _secondaryNavbar: SnComponentOrTemplate;
  private _secondaryNavbarConfig: ISnComponentOrTemplateConfig;
  private _secondaryNavbarWidth: number;

  private _drawerRef: SnDrawerRef;
  private readonly _destroy$ = new Subject<void>();

  constructor(
    private readonly _changeDetectorRef: ChangeDetectorRef,
    private readonly _activatedRoute: ActivatedRoute,
    private readonly _layoutStorage: SnLayoutStorageService,
    private readonly _screen: SnScreenService,
    private readonly _routerHelper: SnRouterHelperService,
    private readonly _drawer: SnDrawerService,
    private readonly _config: SnLayoutNavbarConfig,
    @Attribute('isDark') isDark: boolean,
  ) {
    this.isDark = coerceBooleanProperty(isDark);
  }

  get mode(): SnLayoutNavbarMode {
    return this._config.mode;
  }

  get opened(): boolean {
    return !!this._drawerRef;
  }

  get collapsed(): boolean {
    return this._config.collapsed && !this.isModal;
  }

  get collapseButtonIsHidden(): boolean {
    return this._config.hideCollapseButton || this.isModal;
  }

  get isModal(): boolean {
    return !!this._drawerRef && this._drawerRef.isModal;
  }

  get secondaryNavbar(): SnComponentOrTemplate {
    return this._secondaryNavbar;
  }

  get secondaryNavbarConfig(): Readonly<ISnComponentOrTemplateConfig> {
    return this._secondaryNavbarConfig;
  }

  get secondaryNavbarWidth(): number {
    return this._secondaryNavbarWidth;
  }

  get hasSecondaryNavbar(): boolean {
    return !!this._secondaryNavbar;
  }

  get canSeeSecondaryNavbar(): boolean {
    return this.secondaryNavbar && !this.secondaryNavbarConfig.hidden;
  }

  public setSecondaryNavbarHidden(bool: boolean): void {
    if (!this._secondaryNavbarConfig) {
      return;
    }

    this._secondaryNavbarConfig.hidden = bool;
    this._updateDrawer();
    this._changeDetectorRef.detectChanges();
  }

  public ngOnInit(): void {
    this._layoutStorage.checkInNavbar(this);

    this._watchRouter();
    this._watchScreen();
  }

  public ngOnDestroy(): void {
    this._layoutStorage.checkOfNavbar();

    if (this._drawerRef) {
      this._drawerRef.close();
    }

    this._destroy$.next();
    this._destroy$.complete();
  }

  public open(): void {
    this.toggleOpen(true);
  }

  public close(): void {
    this.toggleOpen(false);
  }

  public toggleOpen(opened: boolean = !this.opened): void {
    if (this.opened !== opened) {
      if (opened) {
        this._openDrawer();
      } else {
        this._closeDrawer();
      }

      this.navbarOpened.next(opened);
      this._changeDetectorRef.markForCheck();
    }
  }

  public collapse(): void {
    this.toggleCollapse(true);
  }

  public expand(): void {
    this.toggleCollapse(false);
  }

  public toggleCollapse(collapsed: boolean = !this._config.collapsed): void {
    if (this._config.collapsed !== collapsed) {
      this._config.update({ collapsed });
      this._updateDrawer();
      this.navbarCollapsed.next(collapsed);

      this._changeDetectorRef.detectChanges();
    }
  }

  public setMode(mode: SnLayoutNavbarMode): void {
    if (this.mode !== mode) {
      this._config.update({ mode });
      this._updateDrawer();

      this._changeDetectorRef.markForCheck();
    }
  }

  public showCollapseButton(): void {
    this.toggleCollapseButtonVisibility(false);
  }

  public hideCollapseButton(): void {
    this.toggleCollapseButtonVisibility(true);
  }

  public toggleCollapseButtonVisibility(hidden: boolean = !this.collapseButtonIsHidden): void {
    if (this.collapseButtonIsHidden !== hidden) {
      this._config.update({ hideCollapseButton: hidden });
      this._changeDetectorRef.markForCheck();
    }
  }

  public initSecondaryNavbar<T, D>(
    componentOrTemplateRef: SnComponentOrTemplate<T>,
    config: ISnComponentOrTemplateConfig<D> = {},
  ): void {
    if (this._secondaryNavbar) {
      throw Error('Attempting to attach secondary navbar after content is already attached');
    }

    this._secondaryNavbar = componentOrTemplateRef;
    this._secondaryNavbarConfig = config;
    this._secondaryNavbarWidth = this._config.width - this._config.collapseWidth;

    this._updateDrawer();

    this._changeDetectorRef.markForCheck();
  }

  public destroySecondaryNavbar(): void {
    this._secondaryNavbar = null;
    this._secondaryNavbarConfig = null;

    this._updateDrawer();

    this._changeDetectorRef.markForCheck();
  }

  public onSeconadyNavbarEnter(): void {
    if (this.collapsed) {
      this._drawerRef.setSize(this._config.width);
    }
  }

  public onSeconadyNavbarLeave(): void {
    if (this.collapsed) {
      this._drawerRef.setSize(72);
    }
  }

  private _openDrawer(): void {
    this._drawerRef = this._drawer.open(this._drawerTmpl, {
      position: 'left',
      panelClass: 'sn-layout-navbar',
      mode: this._config.mode,
      width: this._config.width,
      closeOnNavigation: false,
    });

    this._updateDrawer();

    this._drawerRef.modeChange$
      .pipe(
        filter((mode) => mode === 'modal'),
        takeUntil(this._destroy$),
      )
      .subscribe(() => {
        this.close();
      });

    this._drawerRef.afterClosed$.pipe(takeUntil(this._destroy$)).subscribe(() => {
      this.close();
      this._drawerRef = null;
    });
  }

  private _closeDrawer(): void {
    if (this._drawerRef) {
      this._drawerRef.close();
    }
  }

  private _updateDrawer(): void {
    if (!this._drawerRef) {
      return;
    }

    this._updateDrawerClasses();
    this._updateDrawerMode();
    this._updateDrawerWidth();
  }

  private _updateDrawerClasses(): void {
    const classes = {
      'sn-layout-navbar--expanded': !this.collapsed,
      'sn-layout-navbar--collapsed': this.collapsed,
      'sn-layout-navbar--has-secondary': this.canSeeSecondaryNavbar,
    };

    Object.keys(classes).forEach((className) => {
      const enabled = classes[className];

      if (enabled) {
        this._drawerRef.addPanelClass(className);
      } else {
        this._drawerRef.removePanelClass(className);
      }
    });
  }

  private _updateDrawerMode(): void {
    this._drawerRef.setMode(this._config.mode);
  }

  private _updateDrawerWidth(): void {
    if (this.collapsed && this.canSeeSecondaryNavbar) {
      this._drawerRef.setSize(72);
    } else if (this.collapsed) {
      this._drawerRef.setSize(56);
    } else {
      this._drawerRef.setSize(this._config.width);
    }
  }

  private _setConfig(): void {
    this.setMode(this._config.mode);
    this.toggleCollapse(this._config.collapsed);
    this.toggleCollapseButtonVisibility(this._config.hideCollapseButton);

    this._updateDrawer();
  }

  private _watchScreen(): void {
    this._screen.viewportChange$
      .pipe(
        startWith(this._screen.viewport),
        filter(() => this.mode === 'adaptive'),
        filter((viewport) => viewport === SnViewport.Web),
        takeUntil(this._destroy$),
      )
      .subscribe(() => {
        this.open();
      });
  }

  private _watchRouter(): void {
    this._routerHelper.routeChange$
      .pipe(
        startWith(this._activatedRoute),
        mapToRouteDataItem<ISnLayoutNavbarConfig>('snLayoutNavbar', true),
        takeUntil(this._destroy$),
      )
      .subscribe((config) => {
        this._config.restore();
        this._config.update(config);

        this._setConfig();
      });
  }
}
